/** @format */

import React, { useEffect, useState, useMemo } from "react";
import Customize_Reworked from "./index"; // Import the layout wrapper
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import { Settings, Play, X, Info, HelpCircle } from "lucide-react"; // Added Info, HelpCircle
import { description, title } from "../../constants/tooltipConstants"; // Assuming these exist
import {
	customizeProduct,
	getDrivePrice,
	getValances,
} from "../../actions/blindActions";
import axios from "../../lib/axios"; // Assuming configured axios instance
import ImageZoomWithButton from "../../components/button/ImageZoomWithButton";
import { Toast } from "./Toast"; // Assuming Toast component exists
import { Tooltip } from "./Tooltip"; // Assuming Tooltip component exists
import { Link } from "react-router-dom";

// Helper to get display names for control systems
function getDriveTypeDisplayName(item) {
	if (!item) return "";
	const nameMap = {
		"zero gravity": "Glide Chainless Manual", // Updated name
		chain: "Chain Manual",
		motor: "Automated (Motorized)",
		// Add other mappings if needed
	};
	return nameMap[item.toLowerCase()] || item; // Return mapped name or original
}

// --- Main Control Step Component ---
const Customize_Control = () => {
	/*===============================  VARIABLES  =================================*/
	const dispatch = useDispatch();
	const { productInfo = {}, optionInfo = {} } = useSelector(
		(state) => state.customize?.customizeProduct || {}
	);
	const country = useSelector((state) => state.country);

	// --- Component State ---
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [data, setData] = useState(null); // Holds drive price info { driveInfo: {}, addonInfo: {} }
	const [availableSystems, setAvailableSystems] = useState([]); // Holds compatible system names ['Chain', 'Motor', ...]

	// Selections (initialized from Redux store or defaults)
	const [system, setSystem] = useState(productInfo?.Operation || ""); // e.g., 'Chain', 'Motor'
	const [position, setPosition] = useState(productInfo?.position || "Right"); // Default 'Right'
	const [tube, setTube] = useState(productInfo?.tube || ""); // Tube description from backend

	// Motor Addon States
	const [remote, setRemote] = useState(productInfo?.remote || "No");
	const [charger, setCharger] = useState(productInfo?.charger || "No");
	const [connector, setConnector] = useState(productInfo?.connector || "No");

	// Video Modal State
	const [videoSrc, setVideoSrc] = useState("");
	const [videoOpen, setVideoOpen] = useState(false);
	const [systems, setSystems] = useState([
		"Chain",
		"Motor",
		"Zero Gravity",
		"Chainless",
	]);
	// Toast State (if needed for other errors)
	// const [errorToast, setErrorToast] = useState(false);

	// --- Derived Values ---
	const shadeTitle = useMemo(
		() => (productInfo?.Type ? `${productInfo.Type} Shade` : "").toLowerCase(),
		[productInfo?.Type]
	);
	const fabricType = useMemo(() => {
		// Logic to determine fabric type string for backend (ensure productInfo.Fabric_Code exists)
		const code = productInfo?.Fabric_Code || "";
		return code.includes("Gossamer-") && !code.includes("Pale")
			? code.replace(/\s/g, "")
			: code;
	}, [productInfo?.Fabric_Code]);
	const hemType = useMemo(
		() => productInfo?.Bottom_Bar || "",
		[productInfo?.Bottom_Bar]
	);

	// Function to safely parse and calculate dimension value
	const getDimensionValue = (whole, fraction) => {
		const wholeNum = parseFloat(whole) || 0;
		let fracValue = 0;
		if (fraction && fraction !== "0") {
			try {
				fracValue = new Function(`return ${fraction}`)() || 0;
			} catch (e) {
				console.error("Error evaluating fraction:", fraction, e);
			}
		}
		return wholeNum + fracValue;
	};
	const width = useMemo(
		() => getDimensionValue(optionInfo?.widthWhole, optionInfo?.widthFraction),
		[optionInfo?.widthWhole, optionInfo?.widthFraction]
	);
	const length = useMemo(
		() =>
			getDimensionValue(optionInfo?.lengthWhole, optionInfo?.lengthFraction),
		[optionInfo?.lengthWhole, optionInfo?.lengthFraction]
	);

	/*===============================  USE EFFECT  ================================*/

	// Load compatible systems and prices on mount or when critical inputs change
	useEffect(() => {
		// Ensure necessary info is available before fetching
		if (width > 0 && length > 0 && fabricType && hemType && shadeTitle) {
			loadInitialData();
		} else {
			// Handle missing prerequisites - show error or wait
			setError(
				"Missing necessary information (dimensions, fabric, bottom bar). Please review previous steps."
			);
			setLoading(false);
			console.warn("Prerequisites for loading control data not met:", {
				width,
				length,
				fabricType,
				hemType,
				shadeTitle,
			});
		}
	}, [width, length, fabricType, hemType, shadeTitle]); // Dependencies for initial load

	// Re-fetch tube info and potentially valances whenever the selected 'system' changes
	useEffect(() => {
		// Don't run on initial load (handled by loadInitialData) or if system is not set yet
		if (
			!loading &&
			system &&
			width > 0 &&
			length > 0 &&
			fabricType &&
			hemType &&
			shadeTitle
		) {
			getTubeAndValanceInfo(system);
		}
	}, [system]); // Triggered only when 'system' state changes

	// Dispatch updates to Redux whenever relevant selections change
	useEffect(() => {
		// Avoid dispatching during initial load or if error occurred
		if (loading || error || !system) return;
		dispatchDetails();
	}, [system, position, remote, charger, connector, tube, loading, error]); // Include all state vars that should trigger dispatch

	/*===============================  FUNCTIONS  =================================*/

	// --- Data Fetching ---
	const getCompatibleSystems = async (totalWidth, totalLength) => {
		// Map over the array asynchronously and await the promises
		const filteredData = await Promise.all(
			systems.map(async (operation) => {
				if (
					!hemType.includes("Accubar") &&
					!hemType.includes("Interlude 2.0") &&
					operation === "Zero Gravity"
				) {
					return undefined;
				}

				const jsonData = JSON.stringify({
					width: totalWidth,
					length: totalLength,
					fabric: fabricType,
					bottom_bar: hemType,
					operation: operation,
					motor: operation === "Motor" ? "Stealth Rechargeable Motor" : "None",
					shade: shadeTitle,
					lam_needed: "false",
					company: "Sun Boss",
				});

				const res = await axios.post(
					"https://backend.mysunglow.com/get_tube",
					jsonData
				);

				const data = res.data;

				console.log("json data for operation", operation, jsonData, data);
				if (data?.status !== "Not compatible") {
					console.log("Operation: ", operation);
					return operation;
				} else {
					return undefined;
				}
			})
		);
		// Filter out any undefined values (if any)
		const filteredResult = filteredData.filter(
			(operation) => operation !== undefined
		);

		return filteredResult;
	};

	// Fetches initial compatible systems and price data
	const loadInitialData = async () => {
		setLoading(true);
		setError(null);
		setData(null);
		setAvailableSystems([]);

		try {
			const [systemsResult, drivePricesResult] = await Promise.all([
				getCompatibleSystems(width, length),
				getDrivePrice(width, length), // Fetch prices regardless of system initially
			]);

			if (systemsResult.length === 0) {
				setError(
					"No compatible control systems found for this configuration. Please adjust dimensions or contact support."
				);
				setLoading(false);
				return;
			}

			setAvailableSystems(systemsResult);
			setData(drivePricesResult); // Store price data { driveInfo: {}, addonInfo: {} }

			// --- Post-load State Correction ---
			let correctedSystem = system;

			// 1. Check if the current `system` selection is valid among fetched options
			const currentSystemValid = systemsResult.some(
				(s) => s.toLowerCase() === system?.toLowerCase()
			);

			if (!currentSystemValid || !system) {
				// If current selection is invalid or empty, default to the first compatible option (or 'Chain' if available)
				correctedSystem = systemsResult.includes("Chain")
					? "Chain"
					: systemsResult[0];
				console.log("Correcting Control System selection to:", correctedSystem);
				// Don't set state here directly, let getTubeAndValanceInfo handle it after fetching tube
			}

			// 2. Fetch Tube/Valance info for the (potentially corrected) system
			await getTubeAndValanceInfo(correctedSystem); // This will update state including 'system' if corrected
		} catch (err) {
			console.error("Error loading initial control data:", err);
			setError("Failed to load control options. Please try again.");
		} finally {
			// Set loading false *after* getTubeAndValanceInfo finishes in the success case
			// setLoading(false); // Moved to getTubeAndValanceInfo
		}
	};

	// Fetches tube info and compatible valances for a GIVEN system
	const getTubeAndValanceInfo = async (selectedSystem) => {
		setLoading(true); // Set loading true for this specific fetch
		setError(null); // Clear previous errors related to tube/valance

		const jsonData = JSON.stringify({
			width: width,
			length: length,
			fabric: fabricType,
			bottom_bar: hemType,
			operation: selectedSystem,
			motor: selectedSystem === "Motor" ? "Stealth Rechargeable Motor" : "None", // Adjust if motor type varies
			shade: shadeTitle,
			lam_needed: "false",
			company: "Sun Boss",
		});

		try {
			const tubeRes = await axios.post(
				"https://backend.mysunglow.com/get_tube",
				jsonData
			);
			const tubeData = tubeRes.data;

			console.log("Tube Info for", selectedSystem, ":", tubeData);

			if (
				tubeData?.status === "Not compatible" ||
				!tubeData?.info?.tube?.Description
			) {
				throw new Error(
					`Tube information not compatible or missing for ${selectedSystem}.`
				);
			}

			const fetchedTubeDesc = tubeData.info.tube.Description;
			const driveTypeForValance = tubeData.info.drive?.Drive; // e.g., 'SS38', 'ZG45'
			const maxRud = tubeData.info.tube?.max_rud;

			// Fetch compatible valances based on tube/drive info
			let valanceResult = [];
			if (driveTypeForValance && maxRud !== undefined && productInfo?.Type) {
				try {
					const valancesData = await getValances(
						driveTypeForValance,
						maxRud,
						productInfo.Type.toLowerCase(), // Use original type name 'Roller', 'Zebra' etc.
						width
					);
					valanceResult = valancesData.valanceResult || [];
					console.log(
						"Compatible Valances for",
						selectedSystem,
						":",
						valanceResult
					);
				} catch (valanceError) {
					console.error("Error fetching valances:", valanceError);
					// Decide if this is critical - maybe proceed without valance data?
					// setError("Could not determine compatible valances."); // Optionally set error
				}
			} else {
				console.warn("Missing data required for fetching valances:", {
					driveTypeForValance,
					maxRud,
					type: productInfo?.Type,
				});
			}

			// --- Update State (including system if corrected) ---
			setSystem(selectedSystem); // Update system state
			setTube(fetchedTubeDesc);

			// --- Dispatch Update to Redux ---
			// We dispatch *here* after getting tube/valance info because these depend on the system
			if (productInfo) {
				// Check productInfo exists
				dispatch(
					customizeProduct({
						productInfo: {
							...productInfo,
							Operation: selectedSystem, // Updated system
							tube: fetchedTubeDesc, // Updated tube
							spring: tubeData.info.spring, // Optional spring info
							torque: tubeData.info.tube.needed_torque, // Optional torque info
							valanceArray: valanceResult, // Updated compatible valances
							// Reset motor addons if system is NOT Motor
							remote: selectedSystem === "Motor" ? remote : "No",
							charger: selectedSystem === "Motor" ? charger : "No",
							connector: selectedSystem === "Motor" ? connector : "No",
							// Keep position unless logic dictates otherwise
							position: position,
							error: false, // Clear error on success
						},
						optionInfo: optionInfo,
					})
				);
			}
		} catch (error) {
			console.error(
				`Error fetching tube/valance info for ${selectedSystem}:`,
				error
			);
			setError(
				`Configuration error for ${getDriveTypeDisplayName(
					selectedSystem
				)}. Please adjust dimensions or contact support.`
			);
			// Reset potentially problematic state?
			// setTube('');
			// Maybe revert system selection if the fetch fails critically?
			// setSystem(productInfo.Operation || ''); // Revert to previous Redux state
		} finally {
			setLoading(false); // Stop loading indicator
		}
	};

	// --- Dispatch & Event Handlers ---

	const dispatchDetails = () => {
		if (!productInfo || !system) return; // Guard clause
		dispatch(
			customizeProduct({
				productInfo: {
					...productInfo,
					// Update only the parts relevant to this component's *direct* state changes
					Operation: system,
					position: position,
					tube: tube, // Tube is updated via getTubeAndValanceInfo effect
					// Motor addons
					remote: system === "Motor" ? remote : "No",
					charger: system === "Motor" ? charger : "No",
					connector: system === "Motor" ? connector : "No",
					// valanceArray is updated via getTubeAndValanceInfo effect
				},
				optionInfo: optionInfo,
			})
		);
	};

	// Handle selecting a control system card
	const handleSystemSelect = (selectedSystem) => {
		if (selectedSystem !== system) {
			// Trigger fetching tube/valance info for the new system
			// The useEffect watching 'system' will handle the state update and dispatch
			getTubeAndValanceInfo(selectedSystem);
			// Reset motor addons immediately for UI responsiveness if switching away from Motor
			if (selectedSystem !== "Motor") {
				setRemote("No");
				setCharger("No");
				setConnector("No");
			}
		}
	};

	// Handle opening the video modal
	const playVideo = (item) => {
		const videoMap = {
			Chain: "/videos/SS_animation.mp4",
			Motor: "/videos/Remote_animation.mp4",
			"Zero Gravity": "/videos/ZG_animation.mp4",
			Chainless: "/videos/Chainless_animation.mp4", // Assuming this exists
		};
		setVideoSrc(videoMap[item] || ""); // Set empty src if no mapping found
		setVideoOpen(true);
	};

	// --- Display Helpers ---
	const displayCountryPrice = (price) => {
		// Use optional chaining and provide default 0
		const basePrice = parseFloat(price) || 0;
		const discount1 = parseFloat(productInfo?.Discount) || 0;
		const discount2 = parseFloat(productInfo?.Global_Discount) || 0;
		const factor = 1.5; // Markup/Conversion factor
		const exchangeRate = country === "USA" ? 0.75 : 1;
		const currencyCode = country === "USA" ? "USD" : "CAD";

		const finalPrice =
			basePrice * factor * (1 - discount1) * (1 - discount2) * exchangeRate;

		// Handle potentially very small or zero prices gracefully
		return `$${finalPrice.toFixed(2)} ${currencyCode}`;
	};

	/*===============================  RENDER  ====================================*/
	return (
		// Wrap step content in the layout component
		<Customize_Reworked>
			<div className="space-y-8">
				{/* --- Header --- */}
				<div className="flex items-center justify-between pb-2 border-b border-gray-200">
					<div className="flex items-center gap-2">
						<div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
							<Settings className="w-5 h-5 text-primary" />
						</div>
						<h3 className="text-lg font-medium">Select Control System</h3>
					</div>
					{/* Optional Help Link */}
					<Link
						to="/guides/control-systems"
						className="text-primary text-xs hover:underline ml-auto inline-flex items-center gap-1"
						target="_blank"
						rel="noopener noreferrer"
					>
						<HelpCircle size={14} /> Control Help
					</Link>
				</div>

				{/* --- Video Modal --- */}
				<AnimatePresence>
					{videoOpen && (
						<motion.div
							key="video-modal"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4"
							onClick={() => setVideoOpen(false)} // Close on backdrop click
						>
							{/* Stop propagation prevents backdrop click from triggering */}
							<motion.div
								initial={{ scale: 0.8, opacity: 0 }}
								animate={{ scale: 1, opacity: 1 }}
								exit={{ scale: 0.8, opacity: 0 }}
								transition={{ type: "spring", damping: 15, stiffness: 150 }}
								className="relative bg-black rounded-lg overflow-hidden max-w-3xl w-full shadow-xl"
								onClick={(e) => e.stopPropagation()}
							>
								<video
									src={videoSrc}
									controls
									autoPlay
									// muted // Decide if it should start muted
									className="w-full h-auto display-block"
								>
									Your browser does not support the video tag.
								</video>
								<button
									onClick={() => setVideoOpen(false)}
									className="absolute top-2 right-2 w-8 h-8 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-lg text-gray-700 transition-colors"
									aria-label="Close video player"
								>
									<X className="w-5 h-5" />
								</button>
							</motion.div>
						</motion.div>
					)}
				</AnimatePresence>

				{/* --- Conditional Rendering: Loading, Error, Options --- */}
				<AnimatePresence mode="wait">
					{loading ? (
						<motion.div
							/* Loading indicator */ key="loading"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							className="flex flex-col items-center justify-center h-40 text-gray-500"
						>
							<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-3"></div>
							<p>Checking compatible controls...</p>
						</motion.div>
					) : error ? (
						<motion.div
							/* Error message */ key="error"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							className="flex flex-col items-center justify-center h-40 text-red-600 bg-red-50 p-6 rounded-lg border border-red-200"
						>
							<p className="font-medium text-center">{error}</p>
							<button
								onClick={loadInitialData}
								className="mt-4 text-sm text-primary hover:underline"
							>
								Try Again
							</button>
							<span className="text-sm text-gray-500 mx-2">or</span>
							<Link
								to="/customize/details"
								className="text-sm text-primary hover:underline"
							>
								Go back to Details
							</Link>
						</motion.div>
					) : (
						// --- Control Options Grid ---
						<motion.div
							key="controls"
							initial="initial"
							animate="animate"
							variants={{
								initial: { opacity: 0 },
								animate: { opacity: 1, transition: { staggerChildren: 0.05 } },
							}}
							className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6"
						>
							{(availableSystems || []).map((item) => {
								const isCurrentSystem =
									system?.toLowerCase() === item.toLowerCase();
								const itemDisplayName = getDriveTypeDisplayName(item);
								const itemPrice = data?.driveInfo?.[item] || 0; // Get price from fetched data
								const tooltipTitle = title[item] || itemDisplayName;
								const tooltipDescription =
									description[item] ||
									`Details about the ${itemDisplayName} system.`;

								return (
									<motion.label // Use label for better accessibility
										key={item}
										htmlFor={`control-${item}`}
										variants={{
											initial: { opacity: 0, y: 10 },
											animate: { opacity: 1, y: 0 },
										}}
										whileHover={{
											y: -3,
											boxShadow: "0 4px 10px rgba(0,0,0,0.05)",
										}}
										transition={{ duration: 0.15 }}
										className={`relative p-4 border-2 rounded-xl cursor-pointer transition-all flex flex-col ${
											isCurrentSystem
												? "border-primary bg-primary/5 shadow-md"
												: "border-gray-200 hover:border-gray-300 bg-white"
										}`}
									>
										{/* Image Preview */}
										<div className="relative rounded-lg mb-3 overflow-hidden border border-gray-100 bg-gray-50">
											<ImageZoomWithButton
												item={{
													image: `/images/customizeToolRenders/${item}.png`,
													colour: itemDisplayName,
												}}
												isFabricSelected={isCurrentSystem}
												customize={true}
												hideButton={true} // Hide default zoom button
											/>
											{/* Play Video Button Overlay */}
											<button
												type="button"
												onClick={(e) => {
													e.stopPropagation();
													playVideo(item);
												}} // Prevent card selection on click
												className="absolute top-2 left-2 w-7 h-7 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow text-primary transition-colors"
												aria-label={`Play video about ${itemDisplayName}`}
											>
												<Play className="w-4 h-4 fill-primary" />
											</button>
										</div>

										{/* Content */}
										<div className="space-y-3 mt-auto pt-2">
											{/* Title, Radio, Tooltip */}
											<div className="flex items-start justify-between gap-2">
												<div className="flex items-center gap-2 flex-grow">
													<input
														id={`control-${item}`}
														type="radio"
														name="control"
														value={item}
														checked={isCurrentSystem}
														onChange={() => handleSystemSelect(item)}
														className="w-4 h-4 mt-0.5 text-primary focus:ring-primary/50 focus:ring-offset-1 flex-shrink-0"
													/>
													<span className="font-medium text-sm leading-snug">
														{itemDisplayName}
													</span>
												</div>
												<Tooltip
													title={tooltipTitle}
													content={tooltipDescription}
												>
													<button
														type="button"
														className="w-5 h-5 rounded-full border border-gray-300 flex items-center justify-center text-gray-500 hover:bg-gray-100 flex-shrink-0"
													>
														<Info size={12} />
													</button>
												</Tooltip>
											</div>

											{/* Price Display */}
											<p
												className={`font-medium text-sm ${
													itemPrice > 0 ? "text-primary" : "text-green-600"
												}`}
											>
												{itemPrice > 0
													? `+${displayCountryPrice(itemPrice)}`
													: "Included"}
											</p>

											{/* Additional Options for Motor */}
											{item === "Motor" && isCurrentSystem && (
												<motion.div
													initial={{ opacity: 0, height: 0 }}
													animate={{ opacity: 1, height: "auto" }}
													transition={{ duration: 0.2 }}
													className="space-y-3 pt-3 border-t border-gray-200"
												>
													{[
														{
															name: "Remote",
															state: remote,
															setState: setRemote,
															tooltip: title.Remote,
															priceKey: "remote",
														},
														{
															name: "Charger",
															state: charger,
															setState: setCharger,
															tooltip: title.Charger,
															priceKey: "charger",
														},
														{
															name: "Connector",
															state: connector,
															setState: setConnector,
															tooltip: title.Connector,
															priceKey: "connector",
														},
													].map((addon) => {
														const addonPrice =
															data?.addonInfo?.[addon.priceKey] || 0;
														return (
															<div key={addon.name} className="space-y-1.5">
																<div className="flex items-center justify-between">
																	<div className="flex items-center gap-1">
																		<label
																			htmlFor={`addon-${addon.name}`}
																			className="text-xs font-medium text-gray-600"
																		>
																			{addon.name}
																		</label>
																		<Tooltip
																			title={addon.name}
																			content={
																				addon.tooltip ||
																				`Select 'Yes' to add a ${addon.name}.`
																			}
																		>
																			<button
																				type="button"
																				className="w-4 h-4 rounded-full border border-gray-300 flex items-center justify-center text-gray-500 hover:bg-gray-100"
																			>
																				<Info size={10} />
																			</button>
																		</Tooltip>
																	</div>
																	<span className="text-primary text-xs font-medium">
																		{addonPrice > 0
																			? `+${displayCountryPrice(addonPrice)}`
																			: "N/A"}
																	</span>
																</div>
																<select
																	id={`addon-${addon.name}`}
																	value={addon.state}
																	onChange={(e) =>
																		addon.setState(e.target.value)
																	}
																	onClick={(e) => e.stopPropagation()} // Prevent card selection
																	className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:ring-0 focus:border-primary outline-none appearance-none"
																>
																	<option value="No">No</option>
																	<option value="Yes">Yes</option>
																</select>
															</div>
														);
													})}
												</motion.div>
											)}
										</div>
									</motion.label>
								);
							})}
						</motion.div>
					)}
				</AnimatePresence>

				{/* --- Position Selection --- */}
				{/* Show only if not loading, no error, and selected system requires it */}
				{!loading && !error && system && system !== "Zero Gravity" && (
					<fieldset className="space-y-4 pt-6 border-t border-gray-200">
						<legend className="font-medium mb-2 text-base">
							Control Position
						</legend>
						<p className="text-sm text-gray-600 mb-3">
							Choose the side where the control mechanism (chain or motor) will
							be located.
						</p>
						<div className="flex flex-wrap gap-4">
							{["Left", "Right"].map((pos) => (
								<motion.label
									key={pos}
									htmlFor={`pos-${pos}`}
									whileHover={{ y: -2 }}
									transition={{ duration: 0.15 }}
									className={`max-w-[180px] relative p-4 border-2 rounded-xl cursor-pointer transition-all flex flex-col ${
										position === pos
											? "border-primary bg-primary/5 shadow-md"
											: "border-gray-200 hover:border-gray-300 bg-white"
									}`}
								>
									<div className="rounded-md mb-3 border border-gray-100 bg-gray-50 overflow-hidden">
										<img
											src={`/images/customizeToolRenders/${pos}.png`}
											alt={`Position ${pos}`}
											className="w-full h-auto object-contain"
										/>
									</div>
									<div className="flex items-center gap-2 mt-auto pt-1">
										<input
											id={`pos-${pos}`}
											type="radio"
											name="position"
											value={pos}
											checked={position === pos}
											onChange={() => setPosition(pos)}
											className="w-4 h-4 text-primary focus:ring-primary/50 focus:ring-offset-1"
										/>
										<span className="font-medium text-sm">Position {pos}</span>
										{/* Optional Tooltip for Position */}
										{/* <Tooltip title={`Position ${pos}`} content="..."> <button>?</button> </Tooltip> */}
									</div>
								</motion.label>
							))}
						</div>
					</fieldset>
				)}
			</div>
		</Customize_Reworked>
	);
};

export default Customize_Control;
